import React from 'react';
import { format } from 'date-fns';

const AchievementDetailsPanel = ({ 
  achievement, 
  onEdit, 
  onDelete, 
  onRequestEndorsement,
  onToggleHighlight,
  onAddWitnesses 
}) => {
  if (!achievement) {
    return (
      <div className="flex-1 flex items-center justify-center text-center p-8">
        <div>
          <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <p className="text-sm font-medium text-gray-500">Select an achievement</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    try {
      const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
      return format(new Date(year, month - 1, day), 'MMMM d, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const typeColorMap = {
    'Work Achievement':     'bg-gray-100 text-gray-600',
    'Personal Achievement': 'bg-gray-100 text-gray-600',
    'Learning Achievement': 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header with title and action buttons */}
      <div className="p-4 md:p-6 border-b border-gray-100">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900 leading-snug">
              {achievement.title}
            </h2>
            <p className="text-gray-500 text-sm mt-1">{formatDate(achievement.date)}</p>
          </div>
          
          {/* Action buttons */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => onEdit(achievement)}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Edit"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => onDelete(achievement.id)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-6 pb-6 pt-4">
        {/* Position and Type Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {achievement.position && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {achievement.position.position_title} @ {achievement.position.company_name}
            </span>
          )}
          {achievement.status && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${typeColorMap[achievement.status] || 'bg-gray-100 text-gray-700'}`}>
              {achievement.status}
            </span>
          )}
        </div>

        {/* Highlight + Endorsement section */}
        {achievement.endorsed ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <span className="text-sm text-green-800 font-semibold block">Verified by Manager</span>
                <span className="text-xs text-green-600">This achievement is credible to recruiters</span>
              </div>
            </div>
          </div>
        ) : achievement.is_highlight ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                  </svg>
                </div>
                <div>
                  <span className="text-sm text-amber-800 font-semibold block">Highlight</span>
                  <span className="text-xs text-amber-700">Showing on your public profile · get it verified to add credibility</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
                  onClick={() => onRequestEndorsement(achievement)}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 3.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Request Verification
                </button>
                <button
                  onClick={() => onToggleHighlight(achievement, false)}
                  className="px-2 py-1.5 text-xs text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-colors whitespace-nowrap"
                  title="Remove from highlights"
                >
                  Un-highlight
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <span className="text-sm text-gray-700 font-medium block">Not on your profile yet</span>
                <span className="text-xs text-gray-400">Mark as a highlight to showcase it on your public record</span>
              </div>
              <button
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                onClick={() => onToggleHighlight(achievement, true)}
              >
                <svg className="w-3 h-3 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                </svg>
                Mark as Highlight
              </button>
            </div>
          </div>
        )}

        {/* Description */}
        {achievement.description && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Story</h3>
            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
              {achievement.description}
            </p>
          </div>
        )}

        {/* Images */}
        {achievement.images && achievement.images.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Images</h3>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
              {achievement.images.slice(0, 6).map((image, index) => (
                <div key={image.id} className="relative group">
                  <div className="aspect-square rounded-md overflow-hidden bg-gray-100 cursor-pointer border border-gray-200">
                    <img
                      src={image.urls?.thumbnail || `/api/images/${image.filename}`}
                      alt={image.originalName || `Achievement image ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                      onClick={() => {
                        const fullImageUrl = image.urls?.preview || image.urls?.original || `/api/images/${image.filename}`;
                        window.open(fullImageUrl, '_blank');
                      }}
                    />
                  </div>
                </div>
              ))}
              {achievement.images.length > 6 && (
                <div className="aspect-square rounded-md bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors border border-gray-200">
                  <span className="text-gray-600 text-xs font-medium">
                    +{achievement.images.length - 6}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Team members */}
        {achievement.team && achievement.team.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Team</h3>
            <div className="flex flex-wrap gap-2">
              {achievement.team.map((member, index) => (
                <span
                  key={index}
                  className="text-blue-600 hover:text-blue-800 cursor-pointer text-sm font-medium"
                >
                  {member}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Witnesses */}
        {achievement.witnesses && achievement.witnesses.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Witnesses</h3>
            <div className="space-y-3">
              {achievement.witnesses.slice(0, 3).map((witness) => (
                <div key={witness.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-xs font-medium text-gray-700">
                    {witness.initials}
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">{witness.name}:</p>
                    <p className="text-gray-600 italic">"{witness.quote}"</p>
                  </div>
                </div>
              ))}
              {achievement.witnesses.length > 3 && (
                <p className="text-sm text-gray-500">
                  And {achievement.witnesses.length - 3} more witness{achievement.witnesses.length - 3 !== 1 ? 'es' : ''}...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Skills tags */}
        {achievement.skills && achievement.skills.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {achievement.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AchievementDetailsPanel;
